/**
 * Take a comma-delimited string and converts to a JavaScript Array and trims whitespace
 *
 * @param {string} string - Comma-delimited string, i.e. "item, item2 , item3,item4"
 * @returns {Array}
 *
 */
function commaListToArray(string) {
  return string.split(',').map(item => item.trim());
}

function newlineToArray(string) {
  return string.split('\n').map(i => i.trim()).filter(i => i);
}

/**
 * A CustomFilter object with a list of values to filter, and a type to filter.
 *
 * @param {string|Array} list - list of values to filter by type
 * @param {string} filterName - name of filter to filter list passed in.
 * @returns {CustomFilter} with values property containing a list of values and a type representing the string the values should filter against.
 *
 */
export function CustomFilter(list, filterName) {
  // sdhardesty 6.1.22 eliminate lodash function with pure javascript.
	this.values = list.filter(obj => obj.type === filterName).map(a => a.value);
  this.type = filterName
}

/**
 *
 * @param {CustomFilter} customFilter - the values and type to filter in a ListJS object.
 * @param {listJS item} item - the item in ListJS list that should check for the existence of the filters in the {@link customFilter}.
 * @returns {boolean} boolean - returns true/false if the item contains a term from the filter.
 */
export function checkCustomFilter(customFilter, item) {
  if (customFilter.type === 'yearFilter') {
    return customFilter.values.includes(item.values()[customFilter.type].slice(0,5).trim()) || customFilter.values.length === 0;
  } else if (
    customFilter.type === 'categoryFilter' ||
    customFilter.type === 'deliveryFilter' ||
    customFilter.type === 'dateFilter' ||
    customFilter.type === 'examFilter' ||
    customFilter.type === 'technicalAreaFilter' ||
    customFilter.type === 'productSuiteFilter' ||
    customFilter.type === 'serviceFilter'
  ) {
    return (
      commaListToArray(item.values()[customFilter.type]).map(v =>
        v.replace("&amp;", "&").toUpperCase()
      ).some(v =>
        customFilter.values.map(x => x.toUpperCase()).indexOf(v.toUpperCase()) >= 0
      ) || customFilter.values.length === 0
    )
  } else if (customFilter.type === 'certificateFilter') {
    return (
      newlineToArray(item.values()[customFilter.type]).map(v =>
        v.replace("&amp;", "&").toUpperCase()
      ).some(v =>
        customFilter.values.map(x => x.toUpperCase()).indexOf(v.toUpperCase()) >= 0
      ) || customFilter.values.length === 0
    )
  } else if (customFilter.type === 'durationFilter') {
    if (customFilter.values.filter(v => parseFloat(v) >= 5).length > 0 && parseFloat(item.values()[customFilter.type]) >= 5) {
      return true;
    }
    return customFilter.values.includes(item.values()[customFilter.type].trim()) || customFilter.values.length === 0;
  } else if (customFilter.type === 'lastNameFilter') {
    return customFilter.values.includes(item.values()[customFilter.type].trim().charAt(0)) || customFilter.values.length === 0;
  } else if (customFilter.type === 'careerLocationFilter') {
    return customFilter.values.filter(function (filter) {
      return item.values()[customFilter.type].toLowerCase().includes(filter.toLowerCase());
    }).length > 0 || customFilter.values.length === 0;
  } else {
    return customFilter.values.includes(item.values()[customFilter.type].trim()) || customFilter.values.length === 0;
  }
}

// Typesafe helpers

function isTextControl(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement {
  return !!el && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement);
}

function getElementText(el: Element | null): string | null {
  if (!el) return null;
  if (isTextControl(el)) return el.value;
  return el.textContent?.trim() ?? null;
}

async function copyText(text: string): Promise<void> {
  // Prefer modern Clipboard API
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback for older browsers
  const ta = document.createElement('textarea');
  ta.value = text;
  // Offscreen but selectable
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  ta.style.top = '0';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
}

function resolveCopyText(
  button: HTMLButtonElement,
  scope: ParentNode
): string | null {
  // 1) Honor explicit targets first (data-target, data-clipboard-target, aria-controls)
  const rawSelector =
    button.getAttribute('data-target') ||
    button.getAttribute('data-clipboard-target') ||
    button.getAttribute('aria-controls');

  if (rawSelector) {
    // Accept id or selector; normalize plain ids
    const selector =
      rawSelector.startsWith('#') || rawSelector.startsWith('.')
        ? rawSelector
        : `#${rawSelector}`;
    const target = (scope as Element).querySelector(selector);
    const text = getElementText(target);
    if (text) return text;
  }

  // 2) Look for the nearest textarea.citation in the same container row/card
  // Try sibling-first, then closest ancestor section, then modal-wide.
  const parent = button.parentElement;
  if (parent) {
    const nearby = parent.querySelector('textarea.citation');
    const nearbyText = getElementText(nearby as Element);
    if (nearbyText) return nearbyText;
  }

  const ancestorSection = button.closest<HTMLElement>('[data-citation-container], .citation-block, .citation-section');
  if (ancestorSection) {
    const inSection = ancestorSection.querySelector('textarea.citation');
    const sectionText = getElementText(inSection as Element);
    if (sectionText) return sectionText;
  }

  // 3) Last resort: first textarea.citation in the modal
  const first = (scope as Element).querySelector('textarea.citation');
  return getElementText(first as Element);
}

function autoGrowIfNeeded(textarea: HTMLTextAreaElement): void {
  // Preserve current height if already taller than content
  const current = textarea.clientHeight;
  // Temporarily reset to compute natural scrollHeight
  const prevHeight = textarea.style.height;
  textarea.style.height = 'auto';
  const needed = textarea.scrollHeight;
  // Restore rule: only grow, don't shrink
  if (needed > current) {
    textarea.style.height = `${needed}px`;
  } else {
    textarea.style.height = prevHeight;
  }
}

// Main initialization

window.addEventListener('pageshow', () => {
  const container = document.getElementById('doiModal') ?? document;
  // Wire copy buttons
  const buttons = container.querySelectorAll<HTMLButtonElement>('[id^="copyButton-"]');
  buttons.forEach((btn) => {
    btn.addEventListener('click', async () => {
      const text = resolveCopyText(btn, container);
      if (!text) return;

      const original = btn.textContent ?? 'Copy';
      try {
        await copyText(text);
        btn.textContent = 'Copied';
      } catch {
        btn.textContent = 'Copy failed';
      } finally {
        setTimeout(() => {
          btn.textContent = original || 'Copy';
        }, 2000);
      }
    });
  });

  // Auto-grow citation textareas
  const textareas = container.querySelectorAll<HTMLTextAreaElement>('textarea.citation');
  textareas.forEach((ta) => {
    if (ta.value.trim() !== '' && ta.scrollHeight > 0 && ta.scrollHeight > ta.clientHeight) {
      autoGrowIfNeeded(ta);
    }
  });
});
